import React, { useState, useEffect, useMemo } from 'react';
import { Card, Row, Col, Spinner } from 'react-bootstrap';
import ReactApexChart from 'react-apexcharts';

/**
 * YearComparisonChart Component
 * 
 * Premium year-over-year comparison visualization with:
 * - Single-row horizontal scroll for cards
 * - Fixed footer alignment for "Monthly Avg"
 * - High-impact stat cards
 * - Hybrid chart & validation insights
 */

// ==================== HOOKS ====================

const useWindowSize = () => {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handler = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return width;
};

// ==================== UTILITY FUNCTIONS ====================

const transformYearData = (apiData) => {
    if (!apiData || typeof apiData !== 'object') return [];

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const yearData = [];

    for (const year in apiData) {
        const monthlyData = months.map(month => apiData[year][month] || 0);
        const total = monthlyData.reduce((sum, val) => sum + val, 0);

        yearData.push({
            year: parseInt(year),
            monthlyData,
            total,
            average: Math.round(total / 12)
        });
    }

    yearData.sort((a, b) => a.year - b.year);

    for (let i = 1; i < yearData.length; i++) {
        const current = yearData[i];
        const previous = yearData[i - 1];
        if (previous.total > 0) {
            const growth = ((current.total - previous.total) / previous.total * 100);
            current.yoyGrowth = parseFloat(growth.toFixed(1));
        } else {
            current.yoyGrowth = 0;
        }
    }
    return yearData;
};

const calculateInsights = (yearData) => {
    if (!yearData || yearData.length === 0) return {};

    const insights = { ranking: {} };
    const sortedByTotal = [...yearData].sort((a, b) => b.total - a.total);
    insights.bestYear = { ...sortedByTotal[0] };
    insights.worstYear = { ...sortedByTotal[sortedByTotal.length - 1] };

    sortedByTotal.forEach((year, index) => {
        insights.ranking[year.year] = index + 1;
    });

    const sortedByGrowth = yearData
        .filter(y => y.yoyGrowth !== undefined)
        .sort((a, b) => (b.yoyGrowth || 0) - (a.yoyGrowth || 0));

    if (sortedByGrowth.length > 0) {
        const bestGrowth = sortedByGrowth[0];
        insights.highestGrowth = {
            year: bestGrowth.year,
            percentage: bestGrowth.yoyGrowth,
            from: bestGrowth.year - 1
        };
    }

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let maxValue = 0;

    yearData.forEach(year => {
        year.monthlyData.forEach((value, index) => {
            if (value > maxValue) {
                maxValue = value;
                insights.bestMonth = { month: months[index], year: year.year, value };
            }
        });
    });

    const currentYear = new Date().getFullYear();
    const currentYearData = yearData.find(y => y.year === currentYear);
    if (currentYearData && currentYearData.yoyGrowth !== undefined) {
        insights.currentYearStatus = {
            year: currentYear,
            status: currentYearData.yoyGrowth >= 0 ? 'On Track' : 'Off Track',
            growth: currentYearData.yoyGrowth
        };
    }

    return insights;
};

const getMedal = (rank) => {
    switch (rank) {
        case 1: return { icon: '🥇', color: '#FFD700', bg: '#FFF9C4' };
        case 2: return { icon: '🥈', color: '#C0C0C0', bg: '#F5F5F5' };
        case 3: return { icon: '🥉', color: '#CD7F32', bg: '#FFECB3' };
        default: return null;
    }
};

// ==================== SUB-COMPONENTS ====================

/**
 * Premium Summary Card - Fixed Layout
 */
const SummaryCard = ({ year, insights, isSelected, onClick, isMobile }) => {
    const rank = insights.ranking[year.year];
    const medal = getMedal(rank);
    const isGrowthPositive = year.yoyGrowth >= 0;
    const growthColor = year.yoyGrowth === undefined ? '#999' : (isGrowthPositive ? '#2E7D32' : '#C62828');

    return (
        <div
            onClick={() => onClick(year.year)}
            style={{
                flex: isMobile ? '0 0 auto' : '1',
                minWidth: isMobile ? 130 : 0,
                backgroundColor: 'white',
                borderRadius: '12px',
                border: isSelected ? '2px solid #1E88E5' : '1px solid #E3F2FD',
                opacity: isSelected ? 1 : 0.8,
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: isSelected
                    ? '0 8px 16px rgba(33, 150, 243, 0.15)'
                    : '0 2px 4px rgba(0,0,0,0.04)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                transform: isSelected ? 'translateY(-2px)' : 'none'
            }}
        >
            {/* Top Section: Header & Metric */}
            <div>
                {/* Header */}
                <div style={{ padding: isMobile ? '10px 8px 0 8px' : '12px 10px 0 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <span style={{
                        fontSize: isMobile ? '14px' : '15px',
                        fontWeight: '700',
                        color: isSelected ? '#1565C0' : '#455A64'
                    }}>
                        {year.year}
                    </span>
                    {medal && (
                        <div style={{
                            fontSize: '18px',
                            backgroundColor: medal.bg,
                            border: `1px solid ${medal.color}40`, // Subtle border
                            padding: '4px 8px',
                            borderRadius: '8px',
                            lineHeight: 1,
                            display: 'flex',
                            alignItems: 'center',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }} title={`Rank #${rank} Year`}>
                            {medal.icon}
                        </div>
                    )}
                </div>

                {/* Main Metric */}
                <div style={{ padding: isMobile ? '6px 8px' : '6px 10px' }}>
                    <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '800', color: '#263238', letterSpacing: '-0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {year.total.toLocaleString()}
                    </div>

                    {/* Growth Indicator */}
                    {year.yoyGrowth !== undefined && year.yoyGrowth !== 0 ? (
                        <div style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            color: growthColor,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                            whiteSpace: 'nowrap'
                        }}>
                            <span>{isGrowthPositive ? '↗' : '↘'}</span>
                            {year.yoyGrowth > 0 ? '+' : ''}{year.yoyGrowth}%
                        </div>
                    ) : (
                        <div style={{ height: '17px' }}></div>
                    )}
                </div>
            </div>

            {/* Footer - Average */}
            <div style={{
                marginTop: '8px',
                padding: isMobile ? '8px 8px' : '8px 10px',
                backgroundColor: isSelected ? '#F1F8E9' : '#FAFAFA',
                borderTop: '1px solid #F0F0F0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: '10px'
            }}>
                <span style={{ color: '#78909C', fontWeight: '600', textTransform: 'uppercase' }}>Avg</span>
                <span style={{ fontWeight: '700', color: '#455A64' }}>
                    {year.average.toLocaleString()}
                </span>
            </div>
        </div>
    );
};

/**
 * Premium Insight Item
 */
const InsightItem = ({ icon, label, value, subtext, color, bgColor, isMobile }) => (
    <Col xs={12} sm={6} md={3}>
        <div style={{
            padding: isMobile ? '16px' : '20px',
            backgroundColor: 'white',
            borderRadius: '16px',
            height: '100%',
            border: '1px solid #E3F2FD',
            boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Accent Bar */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', backgroundColor: color }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{
                    width: isMobile ? '36px' : '40px',
                    height: isMobile ? '36px' : '40px',
                    borderRadius: '10px',
                    backgroundColor: bgColor,
                    color: color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isMobile ? '18px' : '20px'
                }}>
                    {icon}
                </div>
                <span style={{ fontSize: '12px', fontWeight: '700', color: '#78909C', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {label}
                </span>
            </div>

            <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '800', color: '#263238', marginBottom: '4px' }}>
                {value}
            </div>

            <div style={{ fontSize: '13px', fontWeight: '600', color: color }}>
                {subtext}
            </div>
        </div>
    </Col>
);

const InsightsGrid = ({ insights, isMobile }) => {
    if (!insights.bestYear) return null;

    return (
        <div style={{ marginTop: isMobile ? '24px' : '40px' }}>
            <h5 style={{ fontSize: '16px', fontWeight: '700', color: '#455A64', marginBottom: '20px', paddingLeft: '4px' }}>
                Executive Summary
            </h5>
            <Row className="g-3">
                <InsightItem
                    icon="🏆"
                    label="Best Performer"
                    value={insights.bestYear.year}
                    subtext={`${insights.bestYear.total.toLocaleString()} total reports`}
                    color="#2E7D32" // Green
                    bgColor="#E8F5E9"
                    isMobile={isMobile}
                />
                {insights.highestGrowth && (
                    <InsightItem
                        icon="🚀"
                        label="Top Growth"
                        value={insights.highestGrowth.year}
                        subtext={`+${insights.highestGrowth.percentage}% growth rate`}
                        color="#1565C0" // Blue
                        bgColor="#E3F2FD"
                        isMobile={isMobile}
                    />
                )}
                {insights.bestMonth && (
                    <InsightItem
                        icon="⭐"
                        label="Peak Volume"
                        value={`${insights.bestMonth.month} ${insights.bestMonth.year}`}
                        subtext={`${insights.bestMonth.value.toLocaleString()} in one month`}
                        color="#F9A825" // Gold
                        bgColor="#FFFDE7"
                        isMobile={isMobile}
                    />
                )}
                {insights.currentYearStatus && (
                    <InsightItem
                        icon="📅"
                        label="Trending"
                        value={insights.currentYearStatus.year}
                        subtext={`${insights.currentYearStatus.growth > 0 ? '+' : ''}${insights.currentYearStatus.growth}% vs prev year`}
                        color={insights.currentYearStatus.growth >= 0 ? '#00838F' : '#C62828'}
                        bgColor={insights.currentYearStatus.growth >= 0 ? '#E0F7FA' : '#FFEBEE'}
                        isMobile={isMobile}
                    />
                )}
            </Row>
        </div>
    );
};

// ==================== MAIN COMPONENT ====================

const YearComparisonChart = ({ yearMonthData }) => {
    const [selectedYears, setSelectedYears] = useState([]);
    const [viewMode, setViewMode] = useState('monthly'); // 'monthly' | 'yearly'
    const [showTrend, setShowTrend] = useState(false); // Toggle for Trend Line

    const width = useWindowSize();
    const isMobile = width < 768;
    const isSmallMobile = width < 500;

    const yearData = useMemo(() => transformYearData(yearMonthData), [yearMonthData]);
    const insights = useMemo(() => calculateInsights(yearData), [yearData]);
    const availableYears = useMemo(() => yearData.map(y => y.year).sort((a, b) => b - a), [yearData]);

    // Initialize with all years
    useEffect(() => {
        if (availableYears.length > 0 && selectedYears.length === 0) {
            setSelectedYears(availableYears);
        }
    }, [availableYears, selectedYears.length]);

    const filteredYearData = useMemo(() =>
        yearData.filter(y => selectedYears.includes(y.year)),
        [yearData, selectedYears]
    );

    // Chart Configuration
    const chartHeight = isSmallMobile ? 240 : (isMobile ? 280 : 400);
    const chartConfig = useMemo(() => {
        const isYearlyView = viewMode === 'yearly';

        // Colors
        const baseColors = ['#2962FF', '#0091EA', '#00B8D4', '#00C853', '#FFD600', '#FF6D00', '#D50000'];
        const yearlyColor = ['#1565C0'];

        const series = isYearlyView
            ? [{
                name: 'Total Reports',
                type: showTrend ? 'area' : 'bar',
                data: filteredYearData.map(y => y.total)
            }]
            : filteredYearData.map(year => ({
                name: year.year.toString(),
                type: showTrend ? 'line' : 'bar',
                data: year.monthlyData
            }));

        const categories = isYearlyView
            ? filteredYearData.map(y => y.year.toString())
            : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        const colors = isYearlyView
            ? yearlyColor
            : baseColors.slice(0, filteredYearData.length);

        return {
            series,
            options: {
                chart: {
                    type: 'line',
                    fontFamily: 'inherit',
                    background: 'transparent',
                    toolbar: { show: false },
                    animations: { enabled: true, easing: 'easeinout', speed: 800 },
                    dropShadow: {
                        enabled: showTrend,
                        top: 2,
                        left: 0,
                        blur: 4,
                        color: '#000',
                        opacity: 0.1
                    }
                },
                colors: colors,
                stroke: {
                    show: true,
                    width: showTrend ? 3 : 1,
                    curve: 'smooth',
                    lineCap: 'round',
                    // Trend Lines: Inherit Main Color (undefined)
                    // Bars: White Border (['#fff'])
                    colors: showTrend ? undefined : ['#fff']
                },
                fill: {
                    // Yearly Trend = Gradient Area
                    // Monthly Trend = Solid Line (Fill type doesn't affect Line, but Opacity might)
                    // Bars = Solid
                    type: (showTrend && isYearlyView) ? 'gradient' : 'solid',
                    gradient: {
                        shade: 'light',
                        type: 'vertical',
                        shadeIntensity: 0.5,
                        inverseColors: false,
                        opacityFrom: 0.6,
                        opacityTo: 0.1,
                        stops: [0, 100]
                    },
                    // KEY FIX: For Monthly Lines, opacity must be 1. 
                    // Opacity 0 was making the line transparent in some renders.
                    opacity: (showTrend && isYearlyView) ? 0.6 : 1
                },
                plotOptions: {
                    bar: {
                        borderRadius: 4,
                        borderRadiusApplication: 'end',
                        columnWidth: isYearlyView ? '45%' : '85%',
                        dataLabels: { position: 'top' },
                        distributed: false,
                    }
                },
                markers: {
                    size: showTrend ? 5 : 0,
                    hover: {
                        size: 7,
                        sizeOffset: 3
                    },
                    // Style: Colored Dot (inherit) with White Border
                    // colors: undefined (inherits series color automatically)
                    strokeColors: '#fff',
                    strokeWidth: 2,
                    strokeOpacity: 1,
                    fillOpacity: 1,
                    shape: "circle",
                },
                dataLabels: {
                    enabled: isYearlyView && !showTrend,
                    offsetY: -20,
                    style: { fontSize: '12px', colors: ['#333'] },
                    formatter: val => val >= 1000 ? (val / 1000).toFixed(1) + 'k' : val
                },
                xaxis: {
                    categories: categories,
                    axisBorder: { show: false },
                    axisTicks: { show: false },
                    labels: {
                        style: { colors: '#64748B', fontSize: '12px', fontWeight: 600, fontFamily: 'inherit' },
                        offsetY: 2
                    },
                    tooltip: { enabled: false }
                },
                yaxis: {
                    labels: {
                        formatter: val => {
                            if (val >= 1000000) return (val / 1000000).toFixed(1) + 'M';
                            if (val >= 1000) return (val / 1000).toFixed(1) + 'k';
                            return val;
                        },
                        style: { colors: '#94A3B8', fontSize: '11px', fontWeight: 500 },
                        offsetX: -5
                    }
                },
                grid: {
                    position: 'back',
                    borderColor: '#F1F5F9',
                    strokeDashArray: 4,
                    xaxis: { lines: { show: false } },
                    yaxis: { lines: { show: true } },
                    padding: { top: 30, right: 0, bottom: 0, left: 20 }
                },
                tooltip: {
                    theme: 'light',
                    style: { fontSize: '12px' },
                    y: { formatter: val => val ? val.toLocaleString() : '0' },
                    shared: true,
                    intersect: false
                },
                legend: {
                    show: !isYearlyView,
                    showForSingleSeries: true,
                    position: 'top',
                    horizontalAlign: 'left',
                    fontSize: '14px',
                    fontWeight: 600,
                    itemMargin: { horizontal: 10, vertical: 5 },
                    markers: { width: 12, height: 12, radius: 4, strokeWidth: 0, offsetY: 0, strokeColor: '#fff', strokeWidth: 2 }
                },
                responsive: [
                    {
                        breakpoint: 768,
                        options: {
                            chart: { height: 280 },
                            xaxis: { labels: { style: { fontSize: '10px' } } },
                            yaxis: { labels: { style: { fontSize: '9px' } } },
                            grid: { padding: { left: 15, top: 20, right: 5, bottom: 0 } },
                            legend: { fontSize: '12px', itemMargin: { horizontal: 6 } }
                        }
                    },
                    {
                        breakpoint: 480,
                        options: {
                            chart: { height: 240 },
                            xaxis: { labels: { style: { fontSize: '9px' } } }
                        }
                    }
                ]
            }
        };
    }, [filteredYearData, viewMode, showTrend]);

    const toggleYear = (year) => {
        setSelectedYears(prev => {
            if (prev.includes(year) && prev.length <= 1) return prev;
            if (prev.includes(year)) return prev.filter(y => y !== year);
            return [...prev, year].sort((a, b) => a - b);
        });
    };

    return (
        <div style={{ marginTop: '20px', marginBottom: '50px' }}>
            {/* Header Section - Always Visible */}
            <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #EEE' }}>
                <h2 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '500', color: '#000000ff', marginBottom: '8px' }}>
                    Year over Year Performance
                </h2>
                <p style={{ fontSize: isMobile ? '14px' : '15px', color: '#546E7A', margin: 0, width: '100%' }}>
                    Compare your annual performance trends and identify key growth patterns by selecting years below.
                </p>
            </div>

            {(!yearMonthData || yearData.length === 0) ? (
                // Loading State
                <div style={{
                    height: '300px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.5) 100%)',
                    borderRadius: '16px'
                }}>
                    <Spinner
                        animation="border"
                        variant="primary"
                        style={{ width: '40px', height: '40px', borderWidth: '3px', color: '#1565C0' }}
                    />
                    <div style={{
                        marginTop: '16px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#546E7A',
                        letterSpacing: '0.5px'
                    }}>
                        GENERATING ANALYTICS...
                    </div>
                </div>
            ) : (
                // Loaded Content
                <>
                    {/* Stats Cards - Single Row Auto-Fit / Horizontal Scroll on Mobile */}
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        marginBottom: '20px',
                        width: '100%',
                        overflowX: isMobile ? 'auto' : 'visible',
                        flexWrap: isMobile ? 'nowrap' : 'wrap',
                        WebkitOverflowScrolling: isMobile ? 'touch' : 'auto'
                    }}>
                        {yearData.map(year => (
                            <SummaryCard
                                key={year.year}
                                year={year}
                                insights={insights}
                                isSelected={selectedYears.includes(year.year)}
                                onClick={toggleYear}
                                isMobile={isMobile}
                            />
                        ))}
                    </div>

                    {/* Main Chart */}
                    <Card style={{
                        border: 'none',
                        borderRadius: '16px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                        padding: isMobile ? '16px' : '24px'
                    }}>
                        <div style={{
                            marginBottom: '24px',
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            justifyContent: 'space-between',
                            alignItems: isMobile ? 'stretch' : 'center',
                            gap: isMobile ? 12 : 0
                        }}>
                            <div>
                                <h5 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: '700', color: '#1A237E', margin: 0 }}>
                                    {viewMode === 'monthly' ? 'Monthly Trend Analysis' : 'Total Yearly Volume'}
                                </h5>
                                <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#90A4AE', marginTop: '4px' }}>
                                    {selectedYears.length} active years • {viewMode === 'monthly' ? 'Comparing seasonality' : 'Comparing total growth'}
                                </div>
                            </div>

                            {/* Controls */}
                            <div style={{ display: 'flex', gap: '12px', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                                {/* Trend Toggle */}
                                <button
                                    onClick={() => setShowTrend(!showTrend)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: isMobile ? '6px 12px' : '8px 16px',
                                        borderRadius: '12px',
                                        border: showTrend ? '1px solid #1E88E5' : '1px solid #E0E0E0',
                                        background: showTrend ? '#E3F2FD' : 'white',
                                        color: showTrend ? '#1565C0' : '#757575',
                                        fontWeight: '600',
                                        fontSize: isMobile ? '12px' : '13px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                    }}
                                >
                                    <span>📈</span>
                                    {showTrend ? 'Hide Trend' : 'Show Trend'}
                                </button>

                                {/* View Mode Toggle */}
                                <div style={{
                                    display: 'flex',
                                    backgroundColor: '#F5F7FA',
                                    padding: '4px',
                                    borderRadius: '12px',
                                    border: '1px solid #E3F2FD'
                                }}>
                                    <button
                                        onClick={() => setViewMode('monthly')}
                                        style={{
                                            padding: isMobile ? '6px 12px' : '8px 16px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            background: viewMode === 'monthly' ? '#1565C0' : 'transparent',
                                            color: viewMode === 'monthly' ? 'white' : '#78909C',
                                            fontWeight: '600',
                                            fontSize: isMobile ? '12px' : '13px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        Monthly
                                    </button>
                                    <button
                                        onClick={() => setViewMode('yearly')}
                                        style={{
                                            padding: isMobile ? '6px 12px' : '8px 16px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            background: viewMode === 'yearly' ? '#1565C0' : 'transparent',
                                            color: viewMode === 'yearly' ? 'white' : '#78909C',
                                            fontWeight: '600',
                                            fontSize: isMobile ? '12px' : '13px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        Yearly
                                    </button>
                                </div>
                            </div>
                        </div>

                        {filteredYearData.length > 0 ? (
                            <ReactApexChart
                                options={chartConfig.options}
                                series={chartConfig.series}
                                type="line"
                                height={chartHeight}
                            />
                        ) : (
                            <div style={{ padding: '60px', textAlign: 'center', color: '#CFD8DC' }}>
                                Select a year to view the chart
                            </div>
                        )}
                    </Card>

                    {/* Insights Section */}
                    <InsightsGrid insights={insights} isMobile={isMobile} />
                </>
            )}
        </div>
    );
};

export default YearComparisonChart;
